/**
 * Formulario de solicitud de re-vinculación (T039; spec US3, escenario 2).
 *
 * El trabajador indica tipo y número de documento; el teléfono aporta su clave pública (creándola
 * si aún no existe), plataforma, modo de visualización y user agent. La API responde `202` exista o
 * no ese documento —para no revelar quién trabaja en qué empresa (escenario 6)—, de modo que esta
 * pantalla **nunca** muestra "no te encontramos": siempre pasa al seguimiento. La única excepción
 * es el `429` del cupo diario (escenario 5): se queda en el formulario y muestra el mensaje del
 * servidor, porque una cuarta solicitud no llegaría a ninguna bandeja.
 *
 * El número se normaliza (trim + mayúsculas) igual que lo hace la API antes de comparar.
 */
import { SendHorizontal } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { DocumentType } from '@fermin/contracts';

import { Button } from '../../components/ui/button';
import { cn } from '../../components/ui/cn';
import { Field } from '../../components/ui/field';
import { Input, INPUT_CLASSES } from '../../components/ui/input';
import { Screen } from '../../components/ui/screen';
import { t } from '../../i18n';

import { RebindDocumentNumberSchema, useRequestRebind } from './rebind-api';
import { setRebindRequest } from './rebind-store';

import type { ApiError } from '../../api/client';

export interface RebindRequestFormProps {
  /** Recibe el `request_ref` opaco del `202`; la pantalla de seguimiento lo consulta. */
  onSubmitted: (ref: string) => void;
  onCancel: () => void;
}

/** Documentos que suelen ser numéricos: teclado numérico en el teléfono. */
const NUMERIC_DOCUMENT_TYPES = new Set<DocumentType>(['DNI']);

/**
 * Texto del error bajo el formulario. En `429` se prefiere el `message` del servidor: distingue el
 * cupo diario de tres solicitudes (`details.reason = 'DAILY_LIMIT'`, "Alcanzaste el máximo de
 * solicitudes por hoy. Contacta a tu administrador.") del techo por IP, que trae el genérico del
 * catálogo. Sin mensaje utilizable se cae al texto local.
 */
export function rebindErrorMessage(error: ApiError): string {
  if (error.is('RATE_LIMITED')) {
    return error.message.trim() || t('access.rebind.form.rateLimited');
  }
  return t('access.rebind.form.error');
}

export function RebindRequestForm({ onSubmitted, onCancel }: RebindRequestFormProps) {
  const [documentType, setDocumentType] = useState<DocumentType>('DNI');
  const [documentNumber, setDocumentNumber] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const request = useRequestRebind();
  const formId = 'rebind-request-form';

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (request.isPending) {
      return;
    }
    const value = documentNumber.trim().toUpperCase();
    if (value.length === 0) {
      setFieldError(t('common.states.required'));
      return;
    }
    const parsed = RebindDocumentNumberSchema.safeParse(value);
    if (!parsed.success) {
      setFieldError(t('access.rebind.form.documentInvalid'));
      return;
    }
    setFieldError(null);
    request.mutate(
      { document_type: documentType, document_number: parsed.data },
      {
        onSuccess: (data) => {
          setRebindRequest({ ref: data.request_ref, requestedAt: new Date().toISOString() });
          onSubmitted(data.request_ref);
        },
      },
    );
  }

  return (
    <Screen
      data-testid="rebind-request"
      title={t('access.rebind.form.title')}
      lead={t('access.rebind.form.lead')}
      footer={
        <>
          {request.isError ? (
            <p role="alert" className="text-fm-sm text-fm-danger-text">
              {rebindErrorMessage(request.error)}
            </p>
          ) : null}
          <Button
            type="submit"
            form={formId}
            loading={request.isPending}
            loadingText={t('common.states.sending')}
            icon={<SendHorizontal aria-hidden="true" size={20} />}
          >
            {t('access.rebind.form.submit')}
          </Button>
          <Button variant="ghost" type="button" onClick={onCancel}>
            {t('common.buttons.back')}
          </Button>
        </>
      }
    >
      <form id={formId} noValidate onSubmit={submit} className="flex flex-col gap-fm-4">
        <Field label={t('access.rebind.form.documentType')} required>
          {(controlProps) => (
            <select
              {...controlProps}
              name="document_type"
              value={documentType}
              disabled={request.isPending}
              onChange={(event) => setDocumentType(event.target.value as DocumentType)}
              className={cn(INPUT_CLASSES, 'h-fm-control-md')}
            >
              {DocumentType.options.map((option) => (
                <option key={option} value={option}>
                  {t(`access.rebind.documentTypes.${option}`)}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field
          label={t('access.rebind.form.documentNumber')}
          help={t('access.rebind.form.documentHelp')}
          error={fieldError ?? undefined}
          required
        >
          {(controlProps) => (
            <Input
              {...controlProps}
              name="document_number"
              value={documentNumber}
              disabled={request.isPending}
              maxLength={32}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              inputMode={NUMERIC_DOCUMENT_TYPES.has(documentType) ? 'numeric' : 'text'}
              onChange={(event) => {
                setFieldError(null);
                request.reset();
                setDocumentNumber(event.target.value.toUpperCase());
              }}
            />
          )}
        </Field>
      </form>
    </Screen>
  );
}
